import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  TextField,
  Switch,
} from "@mui/material";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { SearchFundAutocomplete } from "../../shared/Autocompletes/SearchFundAutocomplete";
import { groupNumericFieldsBySection } from "../../utils/fundsFormatter";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface FundGraphControlPanelProps {
  selectedFunds: FundDataBasic[];
  onChangeFunds: (funds: FundDataBasic[]) => void;
  selectedFields: (keyof FundDataBasic)[];
  onChangeFields: (fields: (keyof FundDataBasic)[]) => void;
  onToggleFundVisibility: (fundId: number) => void;
  isFundVisible: (fundId: number) => boolean;
  initialFundIds: number[];
  initialAmount: number;
  onChangeInitialAmount: (val: number) => void;
  showAmounts: boolean;
  onToggleShowAmounts: (value: boolean) => void;
}

const numericFields: (keyof FundDataBasic)[] = [
  "cumulative_yield_dynamic",
  "monthly_yield",
  "year_to_date_yield",
  "yield_trailing_3_yrs",
  "yield_trailing_5_yrs",
  "cumulative_6m_yield",
  "cumulative_12m_yield",
  "cumulative_24m_yield",
  
  
  "standard_deviation",
  "alpha",
  "sharpe_ratio",
  

  "stock_market_percent",
  "foreign_currency_exposure",
  "foreign_exposure_percent",
  "foreign_currency_exposure_percent",
  "rank_yield_trailing_3_yrs",
  "rank_yield_trailing_5_yrs",
  "rank_cumulative_12m_yield",
  "rank_sharpe_ratio",
  "rank_alpha",
  "rank_standard_deviation",
  "avg_annual_management_fee",
  "avg_deposit_fee",
  "foreign_exposure",
    "stock_market_exposure",
  "deposits",
  "withdrawls",
  "internal_transfers",
  "net_monthly_deposits",
  "total_assets",
  "avg_annual_yield_trailing_3yrs",
  "avg_annual_yield_trailing_5yrs",
  "actuarial_adjustment",
  "liquid_assets_percent",
];

export const FundGraphControlPanel: React.FC<FundGraphControlPanelProps> = ({
  selectedFunds,
  onChangeFunds,
  selectedFields,
  onChangeFields,
  onToggleFundVisibility,
  isFundVisible,
  initialFundIds,
  initialAmount,
  onChangeInitialAmount,
  showAmounts,
  onToggleShowAmounts,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const groupedFields = useMemo(() => groupNumericFieldsBySection(numericFields), []);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const defaultExpanded: Record<string, boolean> = {};
    Object.keys(groupedFields).forEach((section) => {
      defaultExpanded[section] = true;
    });
    setExpandedSections(defaultExpanded);
  }, [groupedFields]);

  const handleToggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleAddFund = (fund: FundDataBasic) => {
    if (!fund?.fund_id) return;
    if (!selectedFunds.some((f) => f.fund_id === fund.fund_id)) {
      onChangeFunds([...selectedFunds, fund]);
    }
    onToggleFundVisibility(fund.fund_id);
    setSearchValue("");
  };

  const handleToggleField = (field: keyof FundDataBasic) => {
    onChangeFields([field]); // רק שדה אחד נבחר בכל פעם
  };

  return (
    <Box p={2} sx={{ mt: 6, direction: "rtl" }}>
      <Typography variant="h6" gutterBottom>
        קרנות נבחרות
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <SearchFundAutocomplete
          value={searchValue}
          onChange={setSearchValue}
          onSelect={handleAddFund}
        />
      </Stack>

      <Box mt={2}>
        {selectedFunds.map((fund) => (
          <FormControlLabel
            key={fund.fund_id}
            control={
              <Checkbox
                checked={isFundVisible(fund.fund_id)}
                onChange={() => onToggleFundVisibility(fund.fund_id)}
              />
            }
            label={
              <>
                {`${fund.fund_name} (${fund.fund_id})`}
                {!initialFundIds.includes(fund.fund_id) && (
                  <Chip label="חדש" size="small" color="info" sx={{ ml: 1 }} />
                )}
              </>
            }
          />
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        שדות לתצוגה
      </Typography>
    <FormControlLabel
      control={
        <Switch
          checked={showAmounts}
          onChange={(e) => onToggleShowAmounts(e.target.checked)}
        />
      }
      label="הצג כסכום השקעה"
    />
      <TextField
        type="number"
        label="השקעה התחלתית"
        value={initialAmount}
        onChange={(e) => onChangeInitialAmount(Number(e.target.value))}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      />

      {Object.entries(groupedFields).map(([section, fields]) => {
        const isExpanded = expandedSections[section] ?? true;
        return (
          <Accordion
            key={section}
            expanded={isExpanded}
            onChange={() => handleToggleSection(section)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              dir="rtl"
              sx={{ flexDirection: "row-reverse" }}
            >
              <Typography sx={{ fontWeight: "bold" }}>{section}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" sx={{ ml: 1 }}>
                {fields.map(({ field, label }) => (
                  <FormControlLabel
                    key={field}
                    control={
                      <Checkbox
                        checked={selectedFields.includes(field)}
                        onChange={() => handleToggleField(field)}
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
